import * as React from 'react';

import Grid from '@mui/material/Grid';

import kebabCase from 'lodash/kebabCase';
import Carousel, { CarouselSlideRenderControlProps } from 'nuka-carousel';

import { CASE_STUDIES } from '../constants';
import { ICaseStudy, IPublisherUser, IStatistic } from '../interfaces';

export interface ICaseStudiesState {
  currentCaseStudyIndex: number;
}

type TRenderPublisherInfo = (users: IPublisherUser[], publisherName: string) => string;
type TRenderStats = (publisherName: string, stat: IStatistic, index: number) => JSX.Element;

type TCarouselControl = (controlProps: CarouselSlideRenderControlProps) => JSX.Element;

const renderLeftArrow: TCarouselControl = ({
  previousSlide,
}: CarouselSlideRenderControlProps): JSX.Element => (
  <button
    className="win-win-partnerships-carousel-button"
    id="win-win-partnerships-carousel-button-left-arrow"
    onClick={previousSlide}
  >
    <i className="win-win-partnerships-carousel-arrow-left" />
  </button>
);

const renderRightArrow: TCarouselControl = ({
  nextSlide,
}: CarouselSlideRenderControlProps): JSX.Element => (
  <button
    className="win-win-partnerships-carousel-button"
    id="win-win-partnerships-carousel-button-right-arrow"
    onClick={nextSlide}
  >
    <i className="win-win-partnerships-carousel-arrow-right" />
  </button>
);

const renderPublisherInfo: TRenderPublisherInfo = (
  users: IPublisherUser[],
  publisherName: string
): string => {
  let names: string;
  if (users.length > 1) {
    names = `${users[0].firstName} & ${users[1].firstName}`;
  } else {
    names = users[0].firstName + users[0].lastName;
  }

  return `${names}${publisherName ? `, ${publisherName.toUpperCase()}` : ''}`;
};

const renderStats: TRenderStats = (
  publisherName: string,
  stat: IStatistic,
  index: number
): JSX.Element => {
  let { title, value } = stat;
  let titleId: string = `win-win-partnerships__stat-${index}-title`;
  let valueId: string = `win-win-partnerships__stat-${index}-value`;

  return (
    <div className="win-win-partnerships__header" key={`${titleId}-title`}>
      <div id={titleId}>{title}</div>
      <div id={valueId}>{value}</div>
    </div>
  );
};

class CaseStudiesCarousel extends React.Component<unknown, ICaseStudiesState> {
  constructor(props: unknown) {
    super(props);
    this.state = {
      currentCaseStudyIndex: 0,
    };
  }

  public render(): JSX.Element {
    let { currentCaseStudyIndex } = this.state;

    return (
      <Grid container={true} justifyContent={'center'} className="win-win-partnerships-container">
        <Grid item={true} xs={10} lg={8}>
          <div className="home-page-header">Win-win partnerships</div>
          <div className="home-page-header__description">
            Here at Skillz we’d be nothing without our Game Creators. Your success is our success.
            Check out the stories below to learn what it’s like working with Skillz.
          </div>
        </Grid>
        <Carousel
          transitionMode={'scroll'}
          slideIndex={currentCaseStudyIndex}
          className="win-win-partnerships-carousel"
          wrapAround={true}
          heightMode={'current'}
          renderCenterLeftControls={renderLeftArrow}
          renderCenterRightControls={renderRightArrow}
          renderBottomCenterControls={null}
        >
          {CASE_STUDIES.map((caseStudies: ICaseStudy, index: number) => (
            <Grid
              container={true}
              className="win-win-partnerships-case-study-container"
              key={`win-win-partnerships-${kebabCase(caseStudies.publisherName)}-container`}
            >
              <Grid item={true} xs={10} sm={4} md={2} className="win-win-partnerships__img">
                {caseStudies.image}
              </Grid>
              <Grid item={true} xs={10} md={8} lg={6} className="win-win-partnerships__description">
                <div className="win-win-partnerships__header-container">
                  {caseStudies.stats.map((stats: IStatistic, statsIndex: number) =>
                    renderStats(caseStudies.publisherName, stats, statsIndex)
                  )}
                </div>
                <div
                  className="win-win-partnerships__case-study"
                  id={`win-win-partnerships__publisher-quote-${index}`}
                >
                  {`“${caseStudies.testimonial}”`}
                </div>
                <div
                  className="win-win-partnerships__publisher-name"
                  id={`win-win-partnerships__publisher-names-${index}`}
                >
                  {renderPublisherInfo(caseStudies.users, caseStudies.publisherName)}
                </div>
                {/* TODO: Remove this conditional when modal is complete */}
                {false && (
                  <div
                    className="win-win-partnerships__case-study"
                    id={`publisher-case-study-${kebabCase(caseStudies.publisherName)}`}
                  >
                    <a href={caseStudies.caseStudyLink}>Download case study</a>
                  </div>
                )}
              </Grid>
            </Grid>
          ))}
        </Carousel>
      </Grid>
    );
  }
}

export { CaseStudiesCarousel };
